import { useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const COLS = [
  { key: 'via',                    label: 'Via',                   type: 'text' },
  { key: 'anticipo_versato',       label: 'Anticipo versato',      type: 'money' },
  { key: 'prezzo_aggiudicazione',  label: 'Prezzo aggiudicazione', type: 'money' },
  { key: 'saldo_da_versare',       label: 'Saldo da versare',      type: 'money' },
  { key: 'anticipo_restituito',    label: 'Anticipo restituito',   type: 'money' },
  { key: 'spese_accessorie',       label: 'Spese accessorie',      type: 'money' },
  { key: 'ristrutturazione',       label: 'Ristrutturazione',      type: 'money' },
  { key: 'prezzo_vendita',         label: 'Prezzo vendita',        type: 'money' },
];

const emptyRow = () => Object.fromEntries(COLS.map((c) => [c.key, '']));

/**
 * Foglio "Aste vinte" — visibile solo super-admin. Editing inline con salvataggio
 * al blur di ogni campo (PUT parziale via POST/PUT), riga nuova sempre presente in coda.
 */
export default function AdminAsteVinte() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState(emptyRow());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/aste-vinte');
      setRows(data.data || []);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_super_admin) load(); }, [user?.is_super_admin]);

  if (user && !user.is_super_admin) {
    return <main className="content"><p>Accesso riservato.</p></main>;
  }

  const totals = useMemo(() => {
    const t = {};
    for (const c of COLS) {
      if (c.type !== 'money') continue;
      t[c.key] = rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
    }
    // margine = prezzo_vendita - (aggiudicazione + saldo + spese + ristrutt - anticipo restituito)
    // NB: modello semplificato — l'utente inserisce a mano
    return t;
  }, [rows]);

  const updateField = async (row, key, value) => {
    // Se la riga non è ancora persistita e non ha via, ignoro
    if (!row.id) return;
    const patch = { ...row, [key]: value === '' ? null : value };
    // Ottimistico
    setRows((prev) => prev.map((r) => (r.id === row.id ? patch : r)));
    try {
      await api.put(`/api/admin/aste-vinte/${row.id}`, patch);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Errore salvataggio');
      load();
    }
  };

  const commitDraft = async (nextKey, nextValue) => {
    const merged = { ...draft, [nextKey]: nextValue };
    setDraft(merged);
    // Salva solo se c'è almeno la via
    if (nextKey === 'via' && (nextValue || '').trim() === '') return;
    if (!merged.via || merged.via.trim() === '') return;
    try {
      const payload = {};
      for (const c of COLS) {
        payload[c.key] = merged[c.key] === '' ? null : merged[c.key];
      }
      const { data } = await api.post('/api/admin/aste-vinte', payload);
      setRows((prev) => [data.data, ...prev]);
      setDraft(emptyRow());
    } catch (e) {
      setErr(e?.response?.data?.error || 'Errore creazione');
    }
  };

  const removeRow = async (row) => {
    if (!confirm('Eliminare questa riga?')) return;
    try {
      await api.delete(`/api/admin/aste-vinte/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      setErr(e?.response?.data?.error || 'Errore eliminazione');
    }
  };

  const eur = (v) => v == null || v === '' ? '' : Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <main className="content">
      <div className="page__header">
        <div className="page__title-wrap">
          <h1 className="page__title">Aste vinte</h1>
          <p className="page__sub">Foglio conti aggiudicazioni — riservato super-admin</p>
        </div>
      </div>

      {err && <div style={errBox}>{err}</div>}

      <div style={tableWrap}>
        <table style={table}>
          <thead>
            <tr>
              {COLS.map((c) => (
                <th key={c.key} style={{ ...th, textAlign: c.type === 'money' ? 'right' : 'left' }}>{c.label}</th>
              ))}
              <th style={{ ...th, width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={COLS.length + 1} style={{ padding: 12 }}>Caricamento…</td></tr>
            ) : (
              <>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {COLS.map((c) => (
                      <td key={c.key} style={{ ...td, textAlign: c.type === 'money' ? 'right' : 'left' }}>
                        <input
                          type={c.type === 'money' ? 'number' : 'text'}
                          step={c.type === 'money' ? '0.01' : undefined}
                          defaultValue={r[c.key] ?? ''}
                          onBlur={(e) => {
                            const v = e.target.value;
                            if (String(r[c.key] ?? '') !== v) updateField(r, c.key, v);
                          }}
                          style={{ ...inp, textAlign: c.type === 'money' ? 'right' : 'left' }}
                        />
                      </td>
                    ))}
                    <td style={td}>
                      <button onClick={() => removeRow(r)} style={delBtn} title="Elimina">×</button>
                    </td>
                  </tr>
                ))}
                {/* Riga nuova sempre in coda */}
                <tr style={{ background: 'oklch(0.985 0 0)' }}>
                  {COLS.map((c) => (
                    <td key={c.key} style={{ ...td, textAlign: c.type === 'money' ? 'right' : 'left' }}>
                      <input
                        type={c.type === 'money' ? 'number' : 'text'}
                        step={c.type === 'money' ? '0.01' : undefined}
                        value={draft[c.key] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [c.key]: e.target.value })}
                        onBlur={(e) => commitDraft(c.key, e.target.value)}
                        placeholder={c.key === 'via' ? '+ Nuova asta…' : ''}
                        style={{ ...inp, textAlign: c.type === 'money' ? 'right' : 'left' }}
                      />
                    </td>
                  ))}
                  <td style={td}></td>
                </tr>
                {/* Totali */}
                {rows.length > 0 && (
                  <tr style={{ fontWeight: 700, background: 'oklch(0.94 0 0)' }}>
                    <td style={td}>Totali</td>
                    {COLS.filter((c) => c.key !== 'via').map((c) => (
                      <td key={c.key} style={{ ...td, textAlign: 'right' }}>{eur(totals[c.key])}</td>
                    ))}
                    <td style={td}></td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

const tableWrap = { maxWidth: '100%', overflowX: 'auto', border: '1px solid oklch(0.92 0 0)', borderRadius: 12, background: 'white', marginTop: 12 };
const table    = { width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 };
const th       = { padding: '10px 8px', borderBottom: '2px solid oklch(0.9 0 0)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: 'oklch(0.4 0 0)', background: 'oklch(0.98 0 0)' };
const td       = { padding: '4px 6px', borderBottom: '1px solid oklch(0.94 0 0)' };
const inp      = { border: '1px solid transparent', background: 'transparent', padding: '6px 8px', width: '100%', fontSize: 13, borderRadius: 4, outline: 'none' };
const delBtn   = { border: 'none', background: 'transparent', fontSize: 18, color: 'oklch(0.5 0.15 25)', cursor: 'pointer', padding: '2px 8px', lineHeight: 1 };
const errBox   = { marginTop: 12, padding: '10px 12px', border: '1px solid oklch(0.7 0.13 25)', background: 'oklch(0.98 0.03 25)', color: 'oklch(0.4 0.15 25)', borderRadius: 8 };
